// src/pages/document/DocumentTemplateCreatePage.tsx

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';
import { FormRow, AutoGrowTextarea } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { documentTypeMap, documentConfidentialityMap, getStatusMapping } from '@/features/document/logic';

/**
 * Document template create page.
 */
export const DocumentTemplateCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useDocument();
  const scope = useScope();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schemes, setSchemes] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    document_type: '',
    default_title_pattern: '',
    default_content: '',
    numbering_scheme: '',
    default_confidentiality: '',
    default_tags: '',
    is_active: true,
  });

  useEffect(() => {
    store.ListNumberingSchemes()
      .then((res: any) => setSchemes(res?.schemes?.schemes ?? []))
      .catch(() => setSchemes([]));
  }, [scope.productId, store]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await store.CreateDocumentTemplate(formData as any);
      navigate('/documents/templates');
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-1.5">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground transition-colors">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/documents/templates" className="text-muted-foreground hover:text-foreground transition-colors">
            {t('document.templates')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.create')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('document.createTemplate')}</h1>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <form onSubmit={handleSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>{t('document.templateDetails')}</CardTitle>
            <CardDescription>{t('document.fillTemplateInfo')}</CardDescription>
          </CardHeader>
          <CardContent className="divide-y divide-border/60">
            <FormRow label={t('document.templateFieldName')} htmlFor="tpl-name">
              <input
                id="tpl-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('document.templateFieldNamePlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.templateFieldDescription')} htmlFor="tpl-desc">
              <input
                id="tpl-desc"
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('document.templateFieldDescriptionPlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.templateFieldDocType')} htmlFor="tpl-type">
              <Select
                id="tpl-type"
                value={formData.document_type}
                onChange={(e) => handleChange('document_type', e.target.value)}
                fullWidth={false}
                className="w-64"
                placeholder={t('document.templateFieldDocTypePlaceholder')}
              >
                {Object.keys(documentTypeMap).map((type) => {
                  const info = getStatusMapping(documentTypeMap, type);
                  return (
                    <option key={type} value={type}>
                      {t(info.labelKey)}
                    </option>
                  );
                })}
              </Select>
            </FormRow>

            <FormRow label={t('document.templateFieldTitlePattern')} htmlFor="tpl-title-pattern" hint={t('document.templateFieldTitlePatternPlaceholder')}>
              <input
                id="tpl-title-pattern"
                type="text"
                value={formData.default_title_pattern}
                onChange={(e) => handleChange('default_title_pattern', e.target.value)}
                placeholder={t('document.templateFieldTitlePatternPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.templateFieldContent')} htmlFor="tpl-content">
              <div className="rounded-md border border-input bg-background px-3 py-2">
                <AutoGrowTextarea
                  id="tpl-content"
                  value={formData.default_content}
                  onChange={(v) => handleChange('default_content', v)}
                  placeholder={t('document.templateFieldContentPlaceholder')}
                  minRows={4}
                />
              </div>
            </FormRow>

            <FormRow label={t('document.templateFieldNumberingScheme')} htmlFor="tpl-scheme">
              <Select
                id="tpl-scheme"
                value={formData.numbering_scheme}
                onChange={(e) => handleChange('numbering_scheme', e.target.value)}
                fullWidth={false}
                className="w-64"
                placeholder={t('document.templateFieldNumberingSchemePlaceholder')}
                options={schemes.map((s) => ({ value: s.entity?.id ?? s.id, label: s.name }))}
              />
            </FormRow>

            <FormRow label={t('document.templateFieldConfidentiality')} htmlFor="tpl-conf">
              <Select
                id="tpl-conf"
                value={formData.default_confidentiality}
                onChange={(e) => handleChange('default_confidentiality', e.target.value)}
                fullWidth={false}
                className="w-64"
                placeholder={t('document.templateFieldConfidentialityPlaceholder')}
              >
                {Object.keys(documentConfidentialityMap).map((conf) => {
                  const info = getStatusMapping(documentConfidentialityMap, conf);
                  return (
                    <option key={conf} value={conf}>
                      {t(info.labelKey)}
                    </option>
                  );
                })}
              </Select>
            </FormRow>

            <FormRow label={t('document.templateFieldTags')} htmlFor="tpl-tags" hint={t('document.templateFieldTagsPlaceholder')}>
              <input
                id="tpl-tags"
                type="text"
                value={formData.default_tags}
                onChange={(e) => handleChange('default_tags', e.target.value)}
                placeholder={t('document.templateFieldTagsPlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <div className="py-2">
              <ToggleSwitch
                label={t('document.templateFieldActive')}
                checked={formData.is_active}
                onChange={(checked) => handleChange('is_active', checked)}
                className="max-w-sm"
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to="/documents/templates">
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button type="submit" disabled={isSubmitting || !formData.name.trim()}>
              {isSubmitting ? t('common.creating') : t('document.createTemplate')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default DocumentTemplateCreatePage;
